import { Box } from "@mui/material";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { useCustomisation } from "../../context/CustomisationContext";
import { useTranslate } from "../../hooks/useTranslate";
import { useAuth } from "../../context/AuthContext";
import { useLanguages } from "../../context/LanguagesContext";
import { useToast } from "../../context/ToastContext";
import { getHomeAssignments } from "../../services/api/apiClient";
import {
  GetHomeAssignmentsParams,
  HomeAssignment,
  UserAssignmentsMode,
} from "../../services/api/types/general/homeAssignment";
import { HomeBanner } from "./components/Dashboard/HomeBanner";
import { MyAssignments } from "./components/MyAssignments/MyAssignments";
import { usePortal } from "../../context/PortalsContext";
import { usePageMetadata } from "../../context/PageMetadataContext";
import { extractErrorMessage } from "../../utils/errorUtils";
import { hasSomeCustomisation } from "../../utils/helpers";
import { usePageCustomisation } from "../../hooks/usePageCustomisation";
import { HomeSkeleton } from "./components/HomeSkeleton";

const Home = () => {
  const { auth } = useAuth();
  const { portals } = usePortal();
  usePageCustomisation("HOME");
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [assignments, setAssignments] = useState<HomeAssignment[]>([]);
  const [allAssignments, setAllAssignments] = useState<HomeAssignment[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const autoOpenAssignmentUid = (location.state as { autoOpenAssignmentUid?: string } | null)
    ?.autoOpenAssignmentUid;

  // Check if we're in the assignments nested route
  const isAssignmentsRoute = location.pathname.startsWith("/home/assignments");
  const { customisations } = useCustomisation();
  const { languages } = useLanguages();
  const t = useTranslate("home");
  const {
    HOME: { textColorsSettings: homeTextColors },
    GPC: { textColorsSettings: gpcTextColors },
  } = customisations.customisations;
  const primaryTextColor =
    homeTextColors?.homePrimaryTextColor ?? gpcTextColors?.gpcPrimaryTextColor;

  const getFiltersFromURL = (): GetHomeAssignmentsParams => {
    const params = new URLSearchParams(location.search);

    return {
      groupLevel: 0,
      ...(params.get("portalGroupId") && {
        portalGroupId: params.get("portalGroupId") || undefined,
      }),
      ...(params.get("type") && {
        type: params.get("type") || undefined,
      }),
      ...(params.get("importance") && {
        importance: params.get("importance") || undefined,
      }),
      ...(params.get("progressStatus") && {
        progressStatus: params.get("progressStatus") || undefined,
      }),
    };
  };

  const [filterParams, setFilterParams] =
    useState<GetHomeAssignmentsParams>(getFiltersFromURL());

  const updateFiltersInURL = (filters: GetHomeAssignmentsParams) => {
    const params = new URLSearchParams(location.search);

    if (filters.portalGroupId) {
      params.set("portalGroupId", filters.portalGroupId);
    } else {
      params.delete("portalGroupId");
    }

    if (filters.type) {
      params.set("type", filters.type);
    } else {
      params.delete("type");
    }

    if (filters.importance) {
      params.set("importance", filters.importance);
    } else {
      params.delete("importance");
    }

    if (filters.progressStatus) {
      params.set("progressStatus", filters.progressStatus);
    } else {
      params.delete("progressStatus");
    }

    navigate(
      `${location.pathname}${params.toString() ? `?${params.toString()}` : ""}`,
      { replace: true },
    );
  };

  const fetchHomeAssignments = async (
    params: GetHomeAssignmentsParams = { groupLevel: 0 },
  ) => {
    if (auth.user && portals.selectedPortal) {
      try {
        // Only send non-group filters to the backend
        const apiParams: GetHomeAssignmentsParams = {
          groupLevel: params.groupLevel,
          ...(params.type && { type: params.type }),
          ...(params.importance && { importance: params.importance }),
          ...(params.progressStatus && {
            progressStatus: params.progressStatus,
          }),
        };

        const data = await getHomeAssignments(
          auth.user.UserPortalUid,
          portals.selectedPortal.portalUid,
          apiParams,
        );
        setAllAssignments(data);

        // Determine preview mode from the unfiltered fetch only
        if (!params.type && !params.importance && !params.progressStatus) {
          setIsPreviewMode(
            data.some((a) => a.mode === UserAssignmentsMode.PREVIEW),
          );
        }

        // Apply client-side group filtering
        const filteredData = applyClientSideGroupFilter(
          data,
          params.portalGroupId,
        );
        setAssignments(filteredData);
      } catch (error: any) {
        // 423 (max attempts exceeded) is assignment-specific and already
        // surfaced on the assignment page itself; don't leak it as a toast
        // here since this page has nothing to do with that assignment.
        if (error?.response?.status !== 423) {
          showToast(
            extractErrorMessage(error, "Failed to fetch assignments"),
            "error",
          );
        }
      }
    }
  };

  // Apply client-side filtering by group IDs
  const applyClientSideGroupFilter = (
    data: HomeAssignment[],
    filterGroupIds: string | undefined,
  ): HomeAssignment[] => {
    if (!filterGroupIds) {
      return data;
    }

    const selectedGroupIds = filterGroupIds.split("|");
    return data.filter((assignment) =>
      selectedGroupIds.includes(assignment.assignmentId),
    );
  };

  const handleFiltersChange = (filters: GetHomeAssignmentsParams) => {
    setFilterParams(filters);
    updateFiltersInURL(filters);

    // When all filters are cleared, always refetch to get full unfiltered data
    const hasNoFilters =
      !filters.portalGroupId &&
      !filters.type &&
      !filters.importance &&
      !filters.progressStatus;
    if (hasNoFilters) {
      fetchHomeAssignments(filters);
    } else if (
      allAssignments.length > 0 &&
      !filters.type &&
      !filters.importance &&
      !filters.progressStatus
    ) {
      // Only group filter changed, filter client-side
      const filteredData = applyClientSideGroupFilter(
        allAssignments,
        filters.portalGroupId,
      );
      setAssignments(filteredData);
    } else {
      fetchHomeAssignments(filters);
    }
  };

  const getAssignmentHref = (assignment: HomeAssignment) => {
    if (assignment.isDirectAssignment) {
      return `/assignment/${assignment.assignmentId}`;
    }
    const params = new URLSearchParams(location.search);
    params.delete("portalGroupId");
    const query = params.toString();
    return `/home/assignments/${assignment.assignmentId}${query ? `?${query}` : ""}`;
  };

  const handleCardClick = (assignment: HomeAssignment) => {
    if (assignment.isDirectAssignment) {
      localStorage.setItem(
        `assignment-mode:${assignment.assignmentId}`,
        assignment.mode,
      );
      navigate(getAssignmentHref(assignment), {
        state: {
          parentPath: "/home",
          mode: assignment.mode,
        },
      });
    } else {
      navigate(getAssignmentHref(assignment), {
        state: {
          rootAssignments: assignments,
          clickedAssignment: assignment,
          initialFilterParams: {
            ...filterParams,
            portalGroupId: undefined,
          },
          homeSearch: location.search,
        },
      });
    }
  };

  useEffect(() => {
    // Only fetch root assignments if we're at the root level
    // Re-fetches on language change too, since titles/group titles come
    // back localized from the API and need to reflect the new language.
    if (auth.user && portals.selectedPortal && !isAssignmentsRoute) {
      const urlFilters = getFiltersFromURL();
      setFilterParams(urlFilters);
      fetchHomeAssignments(urlFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth.user,
    portals.selectedPortal,
    isAssignmentsRoute,
    location.search,
    languages.selectedLanguage,
  ]);

  // Reset breadcrumbs and title when navigating to /home (not in assignments route)
  // This ensures breadcrumbs are cleared every time user navigates to home
  usePageMetadata(
    {
      title: "",
      // Only set empty breadcrumbs when on exact /home route (not in assignments nested route)
      // This will clear any existing breadcrumbs from previous pages
      breadcrumbs: !isAssignmentsRoute ? [] : undefined,
    },
    [location.pathname, isAssignmentsRoute],
  );

  const isHomeLoaded = hasSomeCustomisation(
    customisations.customisations,
    "HOME",
  );

  if (!isHomeLoaded) return <HomeSkeleton />;

  return (
    <Box
      sx={{
        maxWidth: 1440,
        margin: "0 auto",
      }}
    >
      {isAssignmentsRoute ? (
        <Outlet />
      ) : (
        <>
          <Box sx={{ pb: 4 }}>
            <Typography
              color={primaryTextColor}
              marginBottom={4}
              variant="h2"
              fontSize={"32px !important"}
              fontWeight="semi-bold"
              lineHeight={"42px !important"}
            >
              {`${t("header", "welcomeBack")} ${auth.user?.firstName}!`}
            </Typography>
            <HomeBanner />
          </Box>
          <MyAssignments
            assignments={assignments}
            allAssignments={allAssignments}
            onCardClick={handleCardClick}
            getCardHref={getAssignmentHref}
            onFiltersChange={handleFiltersChange}
            groupLevel={0}
            activeFilters={filterParams}
            isPreviewMode={isPreviewMode}
            autoOpenAssignmentUid={autoOpenAssignmentUid}
          />
        </>
      )}
    </Box>
  );
};

export default Home;
